/**
 * ChatMaterialsTable — the in-chat materials → seller-matches table shown when
 * an attachment turn's extracted line-items are sourced against the marketplace
 * (chat document attachments, 6.4).
 *
 * Mobile-first vertical card-stack: each material renders as the SAME
 * `MaterialRow` used on the Materials screen (icon + name + qty/unit + sellers +
 * confidence + "от {min}", expandable to the seller matches), plus a summary
 * footer (`data-testid="materials-total"`) showing the localized "Минимум"
 * label and the formatted `summary.min_total_kgs`.
 *
 * Expand state is local — one row open at a time (matching the Materials
 * screen's behaviour). Row icons are derived exactly like the Materials screen
 * (`buildCategoryBuckets` + `iconForMaterial`) so a category shares an icon.
 *
 * Money is the Decimal-as-JSON **string** convention: `summary.min_total_kgs`
 * is parsed once with `parseDecimal` and formatted with `fmtSom` (locale-aware:
 * `сом` for RU/KY, `som` for EN) — never float math. The `materials` i18n
 * namespace (used by `MaterialRow`) is registered here; the footer label uses
 * the `chat` namespace. Tokens via the `--g-*` mirror.
 */
import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { fmtSom, parseDecimal } from "@/lib/format";
import type { MaterialsEvent } from "@/features/chat/useChatStream";
import { MaterialRow } from "@/features/materials/MaterialRow";
import {
  buildCategoryBuckets,
  iconForMaterial,
} from "@/features/materials/category-buckets";
import { registerMaterialsI18n } from "@/features/materials/api";

export interface ChatMaterialsTableProps {
  materials: MaterialsEvent;
}

/** Narrow the live i18n language to the ru/ky/en MaterialRow expects. */
function rowLang(raw: string): string {
  const base = raw.split("-")[0] ?? "ru";
  return base === "ky" || base === "en" ? base : "ru";
}

export function ChatMaterialsTable({
  materials,
}: ChatMaterialsTableProps): ReactElement {
  // MaterialRow renders strings from the `materials` namespace — ensure the
  // bundle is present (idempotent; init-safe) regardless of import order.
  registerMaterialsI18n();
  const { t, i18n } = useTranslation("chat");
  const lang = rowLang(i18n.language);

  const [expandedId, setExpandedId] = useState<string | null>(null);

  const buckets = useMemo(
    () => buildCategoryBuckets(materials.materials),
    [materials.materials],
  );

  const minTotal = parseDecimal(materials.summary.min_total_kgs);

  return (
    <div
      className="mt-2 flex flex-col gap-2"
      data-testid="chat-materials-table"
    >
      {materials.materials.map((material) => (
        <MaterialRow
          key={material.id}
          material={material}
          lang={lang}
          icon={iconForMaterial(material, buckets)}
          expanded={expandedId === material.id}
          onToggle={() =>
            setExpandedId((cur) => (cur === material.id ? null : material.id))
          }
        />
      ))}

      {/*
       * The total line — the bottom of the slip.
       *
       * It was a third floating card of the same weight as the material rows
       * above it, so the one number the whole table exists to produce read as
       * just another row. A receipt does the opposite: it rules OFF the goods
       * with a heavier line and sets the total at the largest size on the page.
       * The figure is `.g-data` at display weight (tabular, dotted zero) with
       * the unit beside it in body type, so it lines up under any column of
       * prices rendered the same way elsewhere in the product.
       */}
      <div
        data-testid="materials-total"
        className="mt-1 flex items-baseline justify-between gap-4 border-t-2 border-[var(--g-rule-strong)] px-1 pt-3"
      >
        <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--g-ink-2)]">
          {t("table.minTotal")}
        </span>
        <span className="g-data g-stamp-in text-[20px] font-bold leading-none text-[var(--g-figure)]">
          {fmtSom(minTotal, lang)}
        </span>
      </div>
    </div>
  );
}

export default ChatMaterialsTable;
